import type {
  FormDetail,
  FormKind,
  FormMembershipContext,
  FormTiny,
  FormTinyWithStatus,
  PatientSubmissionsTable,
  ReplacedForm,
  VariableCatalogEntry,
} from "@/types/forms/form";

import { getFormsWithStatus, listForms } from "./form";
import {
  getFormMembershipContext,
  getFormOptions,
  getFormTable,
  getPatientSubmissionTable,
  getReplacedForms,
  getVariableCatalog,
  previewReplaceForms,
} from "./form-management";

export const formQueryKeys = {
  all: ["forms"] as const,
  // No kinds is the clinical list, under the key it has always had.
  table: (includeArchived?: boolean, kinds?: FormKind[]) =>
    kinds
      ? ([...formQueryKeys.all, "table", includeArchived, kinds] as const)
      : ([...formQueryKeys.all, "table", includeArchived] as const),
  detail: (formId: string) => [...formQueryKeys.all, "detail", formId] as const,
  submissions: (patientId: string) =>
    [...formQueryKeys.all, "submissions", patientId] as const,
  status: (formPath: string, patientId: string) =>
    [...formQueryKeys.all, "status", formPath, patientId] as const,
  list: () => [...formQueryKeys.all, "list"] as const,
  variableCatalog: () => [...formQueryKeys.all, "variable-catalog"] as const,
  membershipContext: (formId: string) =>
    [...formQueryKeys.all, "membership-context", formId] as const,
  statuses: (patientId: string, formPaths: string[]) =>
    [...formQueryKeys.all, "statuses", patientId, formPaths] as const,
  options: (includeArchived: boolean) =>
    [...formQueryKeys.all, "options", includeArchived] as const,
  replacedForms: (formId: string) =>
    [...formQueryKeys.all, "replaced-forms", formId] as const,
  replacePreview: (formId: string, replacedFormIds: string[]) =>
    [...formQueryKeys.all, "replace-preview", formId, replacedFormIds] as const,
};

export const formQueryOptions = {
  table: (includeArchived?: boolean, kinds?: FormKind[]) => ({
    queryKey: formQueryKeys.table(includeArchived, kinds),
    queryFn: () => getFormTable(includeArchived, kinds),
  }),
  submissions: (patientId: string) => ({
    queryKey: formQueryKeys.submissions(patientId),
    queryFn: () => getPatientSubmissionTable({ patientId }),
    select: (data: PatientSubmissionsTable | null) => data ?? undefined,
  }),
  status: (formPath: string, patientId: string) => ({
    queryKey: formQueryKeys.status(formPath, patientId),
    queryFn: () => getFormsWithStatus([formPath], patientId),
    select: (data: FormTinyWithStatus[] | null) =>
      data?.find((form) => form.pathName === formPath) ?? data?.[0] ?? null,
    enabled: Boolean(formPath && patientId),
    staleTime: 60_000,
  }),
  /** Completion of several forms for one patient, counting forms they replaced. */
  statuses: (patientId: string, formPaths: string[]) => ({
    queryKey: formQueryKeys.statuses(patientId, formPaths),
    queryFn: () => getFormsWithStatus(formPaths, patientId),
    select: (data: FormTinyWithStatus[] | null) => data ?? [],
    enabled: Boolean(patientId && formPaths.length > 0),
    staleTime: 60_000,
  }),
  options: (includeArchived: boolean) => ({
    queryKey: formQueryKeys.options(includeArchived),
    queryFn: () => getFormOptions(includeArchived),
    select: (data: FormTiny[] | null) => data ?? [],
  }),
  replacedForms: (formId: string) => ({
    queryKey: formQueryKeys.replacedForms(formId),
    queryFn: () => getReplacedForms(formId),
    select: (data: ReplacedForm[] | null) => data ?? [],
  }),
  replacePreview: (formId: string, replacedFormIds: string[]) => ({
    queryKey: formQueryKeys.replacePreview(formId, replacedFormIds),
    queryFn: () => previewReplaceForms(formId, replacedFormIds),
    enabled: replacedFormIds.length > 0,
  }),
  list: () => ({
    queryKey: formQueryKeys.list(),
    queryFn: () => listForms(new URLSearchParams()),
    select: (data: FormDetail[] | null) => data ?? [],
  }),
  variableCatalog: () => ({
    queryKey: formQueryKeys.variableCatalog(),
    queryFn: () => getVariableCatalog(),
    select: (data: VariableCatalogEntry[] | null) => data ?? [],
    // Was `Infinity` on the grounds that the catalog is a whitelist compiled into the
    // API. That stopped being true when saved responses joined it: those are tenant
    // data, added on Settings → Suggestions, and nothing there touches this cache — so
    // a snippet created in another tab stayed missing from the picker, and the editor
    // went on warning that a form named a variable that does not exist, until a full
    // reload. Same reasoning as `membershipContext` below.
    staleTime: 0,
  }),
  membershipContext: (formId: string) => ({
    queryKey: formQueryKeys.membershipContext(formId),
    queryFn: () => getFormMembershipContext(formId),
    select: (data: FormMembershipContext | null) => data ?? null,
    enabled: Boolean(formId),
    // Explicitly 0, overriding the client's 5-minute default: both fields turn on
    // state changed elsewhere — the form editor for the tokens, the memberships page
    // for the link — and neither of those touches this cache.
    staleTime: 0,
  }),
};
